import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { catchError, filter, firstValueFrom, Observable, of, switchMap, throwError } from 'rxjs';
import { Ambito } from 'src/app/services/ambiti/ambiti.service';
import { BilancioService } from 'src/app/services/bilancio/bilancio.service';
import { ImportazioniService } from 'src/app/services/importazioni/importazioni.service';
import { ValoriKpi } from 'src/app/services/KPI/kpi.service';
import { ReportVsmeService } from 'src/app/services/report-vsme/report-vsme.service';
import { TemaService } from 'src/app/services/tema/tema.service';
import { OpzioneTematica, Tematica } from 'src/app/services/tematiche/tematiche.service';
import {
  UnitaDiMisura,
  UnitaDiMisuraService,
} from 'src/app/services/unita-di-misura/unita-di-misura.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { Filters } from '../lista-tabellare/classes/lista-tabellare-data-source';
import {
  TabellaCfgDinamicaComponent,
  TabellaDinamica,
} from '../tabella-cfg-dinamica/tabella-cfg-dinamica.component';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TipoCompilazione = 'BILANCIO' | 'PIANO' | 'SINTESI' | 'POLICY' | 'REPORT_VSME';
export type TipoElemento =
  | 'Impegno'
  | 'Kpi'
  | 'Impatto'
  | 'AzioniEBuonePratiche'
  | 'Certificazioni';
export type TipoAzioneChiave = 'RIDURRE' | 'RAGGIUNGERE' | 'AUMENTARE';

interface FileDaCaricare {
  dati: {
    idAzienda: string;
    idContesto: string;
    idAmbito: string;
    idTematica: string;
    idElemento: string;
  };
  file: File | undefined;
  url: string | undefined;
}

interface TipoAzione {
  chiave: TipoAzioneChiave;
  descrizione: string;
}

type EtichetteTipi = Record<TipoElemento, string>;

// ─── Constants ────────────────────────────────────────────────────────────────

const ETICHETTE_TIPI_BILANCIO: EtichetteTipi = {
  Impegno: 'Impegno aziendale',
  Kpi: 'Indicatore quantitativo [KPI]',
  Impatto: 'Impatto',
  AzioniEBuonePratiche: 'Azione e buona pratica aziendale',
  Certificazioni: 'Certificazione aziendale',
};

const ETICHETTE_TIPI_PIANO: EtichetteTipi = {
  Impegno: 'Impegno aziendale',
  Kpi: 'Target quantitativo',
  Impatto: 'Impatto',
  AzioniEBuonePratiche: 'Target qualitativo',
  Certificazioni: 'Certificazione aziendale',
};

const TIPI_AZIONE: TipoAzione[] = [
  { chiave: 'RIDURRE', descrizione: 'Ridurre' },
  { chiave: 'RAGGIUNGERE', descrizione: 'Raggiungere' },
  { chiave: 'AUMENTARE', descrizione: 'Aumentare' },
];

const ELEMENTI_DA_COMPILARE = ['impegni', 'azioni', 'kpis', 'certificazioni'] as const;
const TIPI_IMMAGINE_ACCETTATI = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/svg+xml',
]);
const MAX_FILE_SIZE_BYTES = 1_048_576; // 1 MB

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-compila-sintesi-sostenibilita',
  templateUrl: './compila-sintesi-sostenibilita.component.html',
  styleUrls: ['./compila-sintesi-sostenibilita.component.scss'],
  standalone: false,
})
export class CompilaSintesiSostenibilitaComponent implements AfterViewInit, OnInit {
  // ─── Injected Services ──────────────────────────────────────────────────────

  private readonly fb = inject(FormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);
  private readonly utilityService = inject(UtilityService);
  private readonly bilancioService = inject(BilancioService);
  private readonly reportVsmeService = inject(ReportVsmeService);
  private readonly importazioniService = inject(ImportazioniService);
  private readonly temaService = inject(TemaService);
  private readonly unitaDiMisuraService = inject(UnitaDiMisuraService);

  // ─── Inputs ─────────────────────────────────────────────────────────────────

  readonly compilazione = input.required<any>();
  readonly esercizio = input<number | undefined>();
  readonly idSintesiSostenibilita = input.required<string>();
  readonly titolo = input<string>('');
  readonly isAnteprima = input<boolean>(false);
  readonly isCompEsterna = input<boolean>(false);
  readonly valueToggleAnni = input<boolean>(false);
  readonly temporaryToken = input<string | undefined>(undefined);
  readonly isPermessoSoloRead = input<boolean | undefined>(false);
  readonly tipo = input<TipoElemento | undefined>(undefined);
  readonly salvaCompilazione =
    input.required<(compilazione: any, isImportazione?: boolean) => Observable<any>>();

  readonly landingPageRaw = input<string>('', { alias: 'landingPage' });

  // ─── Outputs ────────────────────────────────────────────────────────────────

  readonly chiudoCompilazione = output<void>();
  readonly loading = output<boolean>();

  // ─── ViewChild ──────────────────────────────────────────────────────────────

  @ViewChild('cfgTabellaDinamica') cfgTabellaDinamica?: TabellaCfgDinamicaComponent;

  // ─── Signals ────────────────────────────────────────────────────────────────

  readonly showTouchedField = signal(false);
  readonly mostraCampiTarget = signal(false);
  readonly targetRequired = signal(false);
  readonly targetConsigliato = signal(false);
  readonly descrizioneErrore = signal<string | undefined>(undefined);
  readonly isIconaGuida = signal(false);
  readonly urlPlaceholder = signal('assets/images/logo/logo-sostenibile.png');
  readonly selectedTipoAzione = signal<TipoAzioneChiave>(TIPI_AZIONE[0].chiave);

  // ─── Computed ───────────────────────────────────────────────────────────────

  readonly landingPage = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.landingPageRaw()),
  );

  readonly ambiti = computed<Ambito[]>(() => this.compilazione()?.ambiti ?? []);

  readonly isPMI = computed(() => this.compilazione()?.tipoAzienda === 'PMI');

  readonly etichetteTipi = computed<EtichetteTipi | undefined>(() => {
    switch (this.compilazione()?.tipo as TipoCompilazione) {
      case 'PIANO':
        return ETICHETTE_TIPI_PIANO;
      case 'BILANCIO':
        return ETICHETTE_TIPI_BILANCIO;
      default:
        return undefined;
    }
  });

  readonly totTematiche = computed(() =>
    this.ambiti().reduce((acc, ambito) => acc + ambito.tematiche.length, 0),
  );

  // ─── Public State ───────────────────────────────────────────────────────────

  readonly tipoAzione = TIPI_AZIONE;

  formCompilazione!: FormGroup;
  currentSlideIndex = 0;
  titoloBtnAvanti = 'Avanti';
  indexAmbitoCorrente = 0;
  indexTematicaCorrente = 0;
  arrayUm: Record<string, UnitaDiMisura[]> = {};
  arrayAnni: number[] = [];
  tabellaCache: Record<string, TabellaDinamica> = {};

  // ─── Private State ──────────────────────────────────────────────────────────

  private readonly _mappaFileDaCaricare: Record<string, FileDaCaricare> = {};

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.temaService.temaUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.urlPlaceholder.set(this.temaService.logoPath));

    const tipoCompilazione = this.compilazione().tipo as TipoCompilazione;

    if (tipoCompilazione === 'BILANCIO') {
      this._valutaTargetObbligatorio();
    }

    if (tipoCompilazione === 'PIANO' && !this.isCompEsterna()) {
      this.targetRequired.set(true);
    }

    this._buildForm();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const esercizio = this.esercizio();
      if (esercizio) {
        this.arrayAnni = Array.from({ length: 5 }, (_, i) => esercizio - i);
      }

      for (const ambito of this.ambiti()) {
        for (const tematica of ambito.tematiche) {
          if (this.compilazione().tipo === 'REPORT_VSME') {
            tematica.risposteVsme?.forEach((rv: any) =>
              rv.sottoOpzioni?.forEach((s: any) => this._caricaUnitaDiMisura(s)),
            );
          } else {
            tematica.kpis?.forEach((kpi: any) => this._caricaUnitaDiMisura(kpi));
          }
        }
      }
    });
  }

  // ─── Public Methods ─────────────────────────────────────────────────────────

  salva(): void {
    this.showTouchedField.set(false);
    const comp = this.compilazione();
    comp.ambiti = this.formCompilazione.get('ambiti')?.getRawValue();

    if (comp.tipo === 'REPORT_VSME') {
      if (this.cfgTabellaDinamica?.colonne.length) {
        this.cfgTabellaDinamica.salvataggioTabella.emit(this.cfgTabellaDinamica.salva());
      }

      const isRispostaRequiredCompilata = this._isRispostaRequiredCompilata(comp);
      if (!isRispostaRequiredCompilata) {
        this.showTouchedField.set(true);
        this._markAllAsTouched();
        return;
      }
    }

    if (this.formCompilazione.status !== 'VALID') {
      this._markAllAsTouched();
      return;
    }

    if (comp.tipo === 'BILANCIO' && this.targetRequired()) {
      if (this._hasTargetErrors(comp)) return;
    }

    this.loading.emit(true);

    this._caricaFiles(Object.values(this._mappaFileDaCaricare), 0)
      .pipe(
        switchMap((success) => {
          if (!success) return throwError(() => 'Errore durante il caricamento delle immagini');
          return this.salvaCompilazione()(comp);
        }),
        catchError((err) => {
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: err?.error?.message ?? "Errore nell'importazione",
            bottoni: [{ nome_btn: 'Chiudi' }],
          });
          this.loading.emit(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loading.emit(false));
  }

  chiudi(): void {
    if (this.isAnteprima() || this.isCompEsterna()) {
      window.close();
    } else {
      this.chiudoCompilazione.emit();
    }
  }

  onRadioChange(event: any, idxAmb: number, idxTem: number): void {
    const tematica = this.ambiti()[idxAmb].tematiche[idxTem];
    tematica.risposte = [event.value];
    tematica.opzioni.forEach((opt: any, idx: number) => {
      if (opt.chiave !== event.value) {
        tematica.opzioni[idx].sottoRisposte = [];
      }
    });
  }

  cambioCheck(event: any, idxAmb: number, idxTem: number, idxOpz: number, chiave: string): void {
    const opzione = this.ambiti()[idxAmb].tematiche[idxTem].opzioni[idxOpz];
    const sottoRisposte: string[] = opzione.sottoRisposte ?? [];
    opzione.sottoRisposte = event.checked
      ? [...sottoRisposte, chiave]
      : sottoRisposte.filter((s) => s !== chiave);
  }

  checkSotto(sottoRisposte: string[] | undefined, chiave: string): boolean {
    return sottoRisposte?.includes(chiave) ?? false;
  }

  visualizzaCampiTarget(kpis: FormGroup): void {
    (kpis.controls['valoriTarget'] as FormArray).push(
      this.fb.group({
        valoreAttribuito: new FormControl(
          { value: undefined, disabled: this.isPermessoSoloRead() ?? false },
          this.targetRequired() ? [Validators.required] : [],
        ),
        idUnitaDiRiferimento: new FormControl(
          { value: '', disabled: this.isPermessoSoloRead() ?? false },
          this.targetRequired() ? [Validators.required] : [],
        ),
        idCategoriaUnitaDiMisura: new FormControl(kpis.controls['idCategoriaUnitaDiMisura'].value),
        anno: new FormControl(
          {
            value: this.compilazione().esercizio[0] + 1,
            disabled: this.isPermessoSoloRead() ?? false,
          },
          this._buildValidatori('anno', 'valore_target'),
        ),
        tipo: new FormControl('valore_target'),
        tipoAzione: new FormControl(
          { value: undefined, disabled: this.isPermessoSoloRead() ?? false },
          this.targetRequired() ? [Validators.required] : [],
        ),
      }),
    );
  }

  eliminaAnniPrecedenti(valoriKpi: FormArray, index: number): void {
    valoriKpi.removeAt(index);
  }

  cambiaUnitaMisuaraAnniPrecedenti(
    valoriKpi: FormArray,
    event: MatOptionSelectionChange,
    isFirst: boolean,
  ): void {
    if (!isFirst || !event.isUserInput || valoriKpi.controls.length <= 1 || !event.source.value)
      return;

    valoriKpi.controls.forEach((kpi, idx) => {
      if (idx > 0) kpi.get('idUnitaDiRiferimento')?.setValue(event.source.value);
    });
  }

  slideMotivazioneChanged(event: MatSlideToggleChange, sottoOpzioni: FormGroup): void {
    const tipo = sottoOpzioni.get('tipo')?.value as string;
    const isNotRisposta = event.checked;

    const toggleValidators = (control: AbstractControl | null): void => {
      if (!control) return;
      isNotRisposta ? control.clearValidators() : control.addValidators(Validators.required);
      control.updateValueAndValidity();
    };

    switch (tipo) {
      case 'kpi': {
        const valoriKpi = sottoOpzioni.get('valoriKpi') as FormArray;
        toggleValidators(valoriKpi?.controls[0]?.get('valoreAttribuito') ?? null);
        break;
      }
      case 'azione':
      case 'checkbox':
        toggleValidators(sottoOpzioni.get('risposta'));
        break;
    }
  }

  generaOggettoTabella(sottoOpzioni: any): TabellaDinamica {
    const id = sottoOpzioni?.id;
    if (!this.tabellaCache[id]) {
      this.tabellaCache[id] = {
        colonne: sottoOpzioni.colonne,
        id,
        righe: sottoOpzioni.righe,
        totaleColonne: sottoOpzioni.totaleColonne,
        totaleRighe: sottoOpzioni.totaleRighe,
        titoloTabella: sottoOpzioni.titoloTabella,
      };
    }
    return this.tabellaCache[id];
  }

  inserisciTabellaNellaForm(tabella: TabellaDinamica, sottoOpzioni: FormArray): void {
    sottoOpzioni.get('colonne')?.setValue(tabella.colonne);
    sottoOpzioni.get('righe')?.setValue(tabella.righe);
    sottoOpzioni.updateValueAndValidity();
  }

  async fileSelezionato(
    file: File[],
    idAmbito: string,
    idTematica: string,
    idCertificazione: string,
    fileInput: HTMLInputElement,
  ): Promise<void> {
    if (!idCertificazione) return;

    const selectedFile = file[0];

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      const dialog = this.utilityService.openDialog({
        titolo: 'Dimensione file eccessiva',
        descrizione: 'La dimensione del file supera 1 Mb, la vuoi ridimensionare?',
        bottoni: [
          { nome_btn: 'No', id_btn: 'N' },
          { nome_btn: 'Si', id_btn: 'S' },
        ],
      });

      const val = await firstValueFrom(dialog.beforeClosed());
      if (val === 'N') return;
    }

    if (!TIPI_IMMAGINE_ACCETTATI.has(selectedFile.type)) {
      const msg =
        "Attenzione! Il file d'importazione deve essere un file immagine (.png .jpeg .jpg .gif .svg)";
      this.utilityService.openSnackBar(msg, '', { duration: 5000, panelClass: ['red-snackbar'] });
      this.descrizioneErrore.set(msg);
      this.isIconaGuida.set(true);
      return;
    }

    this._addFileDaCaricare(
      this._getIdContesto(),
      idAmbito,
      idTematica,
      idCertificazione,
      selectedFile,
    );
    fileInput.value = '';
  }

  eliminaImg(idAmbito: string, idTematica: string, idCertificazione: string): void {
    if (idCertificazione) {
      this._addFileDaCaricare(
        this._getIdContesto(),
        idAmbito,
        idTematica,
        idCertificazione,
        undefined,
      );
    }
  }

  getTooltip(preCompilazioneInfos?: string): string {
    if (!preCompilazioneInfos) return '';

    const [codice, anno] = preCompilazioneInfos.split('_');
    const tooltipMap: Record<string, string> = {
      PA: `Risposta proposta dalla Policy di Sostenibilità dell'anno ${anno} precedentemente associata`,
      SA: `Risposta proposta dalla Sintesi di Sostenibilità dell'anno ${anno} precedentemente associata`,
      AA: `Risposta proposta dall'Assessment dell'anno ${anno} precedentemente associato`,
      BA: `Risposta proposta dal Report di Sostenibilità dell'anno ${anno} precedentemente associato`,
      BANN: `Risposta proposta dal Report di Sostenibilità dell'anno ${anno}`,
      SANN: `Risposta proposta dalla Sintesi di Sostenibilità dell'anno ${anno}`,
      AANN: `Risposta proposta dall'Assessment dell'anno ${anno}`,
    };

    return tooltipMap[codice] ?? '';
  }

  getUrlImg(
    idAmbito: string,
    idTematica: string,
    idCertificazione: string,
    url: string | undefined,
  ): string {
    const key = this._buildFileKey(this._getIdContesto(), idAmbito, idTematica, idCertificazione);
    const datiFile = this._mappaFileDaCaricare[key];

    if (datiFile) return datiFile.url ?? '';

    if (url) {
      if (this.isCompEsterna() && this.temporaryToken()) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}temporaryToken=${this.temporaryToken()}`;
      }
      return url;
    }

    return this.urlPlaceholder();
  }

  isFilePresente(
    idAmbito: string,
    idTematica: string,
    idCertificazione: string,
    urlFileCert: string | undefined,
  ): boolean {
    const key = this._buildFileKey(this._getIdContesto(), idAmbito, idTematica, idCertificazione);
    const datiFile = this._mappaFileDaCaricare[key];
    return datiFile ? !!datiFile.url : !!urlFileCert;
  }

  checkIfNumber(event: KeyboardEvent): boolean {
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  get ambitiFormArray(): FormArray {
    return this.formCompilazione?.get('ambiti') as FormArray;
  }

  // ─── Form Building ──────────────────────────────────────────────────────────

  /**
   * Costruzione del form Array principale con struttura dinamica sugli ambiti
   */
  private _buildForm(): void {
    this.formCompilazione = this.fb.group({
      ambiti: this.fb.array(
        this.compilazione().ambiti?.map((ambito: any) => this._createAmbitoGroup(ambito)) ?? [],
      ),
    });
  }

  /**
   * Creazione ricorsiva dei FormGroup per ambiti, tematiche e elementi, con gestione dinamica di array e validatori
   * @param ambito
   * @returns
   */
  private _createAmbitoGroup(ambito: any): FormGroup {
    const config: Record<string, any> = {};
    for (const [key, value] of Object.entries(ambito)) {
      config[key] =
        key === 'tematiche'
          ? this.fb.array((value as any[]).map((t) => this._createTematicaGroup(t)))
          : new FormControl(value);
    }
    return this.fb.group(config);
  }

  /**
   * Creazione ricorsiva dei FormGroup per tematiche e relativi elementi, con gestione dinamica di array e validatori
   * @param tematica
   * @returns
   */
  private _createTematicaGroup(tematica: any): FormGroup {
    const config: Record<string, any> = {};
    for (const [key, value] of Object.entries(tematica)) {
      if (['impegni', 'azioni', 'kpis', 'certificazioni'].includes(key)) {
        config[key] = this.fb.array((value as any[]).map((t) => this._createElementoGroup(t)));
      } else if (key === 'risposteVsme' && this.compilazione().tipo === 'REPORT_VSME') {
        config[key] = this.fb.array((value as any[]).map((t) => this._createRispostaVsmeGroup(t)));
      } else {
        config[key] = new FormControl(value);
      }
    }
    return this.fb.group(config);
  }

  /**
   * Creazione dei FormGroup per elementi, con gestione dinamica di array e validatori, inclusa logica specifica per campi target e risposte PMI
   * @param elemento
   * @returns
   */
  private _createElementoGroup(elemento: any): FormGroup {
    const config: Record<string, any> = {};
    for (const [key, value] of Object.entries(elemento)) {
      if (key === 'valoriKpi' || key === 'valoriTarget') {
        config[key] = this.fb.array(
          (value as any[]).map((t, idx) =>
            this._createValoreKpiTargetGroup(
              t,
              idx,
              key,
              elemento.moduloCompleto,
              elemento.notRisposta,
            ),
          ),
        );
      } else if (
        key === 'anno' &&
        elemento?.tipo === 'AzioniEBuonePratiche' &&
        this.compilazione().tipo === 'PIANO'
      ) {
        config[key] = new FormControl(
          { value, disabled: this.isPermessoSoloRead() ?? false },
          this._buildValidatori(key, elemento?.tipo),
        );
      } else if (
        key === 'risposta' &&
        this.isPMI() &&
        !elemento.moduloCompleto &&
        !elemento.notRisposta
      ) {
        config[key] = new FormControl({ value, disabled: this.isPermessoSoloRead() ?? false }, [
          Validators.required,
          UtilityService.blankValidator,
        ]);
      } else {
        config[key] = new FormControl(
          { value, disabled: this.isPermessoSoloRead() ?? false },
          this._buildValidatori(key),
        );
      }
    }
    return this.fb.group(config);
  }

  private _createRispostaVsmeGroup(rispostaVsme: any): FormGroup {
    const config: Record<string, any> = {};
    for (const [key, value] of Object.entries(rispostaVsme)) {
      config[key] =
        key === 'sottoOpzioni'
          ? this.fb.array((value as any[]).map((t) => this._createElementoGroup(t)))
          : new FormControl(value);
    }
    return this.fb.group(config);
  }

  private _createValoreKpiTargetGroup(
    valoreKpiTarget: any,
    idx: number,
    parentKey: string,
    isSottoOpzModuloCompleto?: boolean,
    notRisposta?: boolean,
  ): FormGroup {
    const config: Record<string, any> = {};
    for (const [key, value] of Object.entries(valoreKpiTarget)) {
      /** Construisco i validatori  */
      const validatori = this._buildValidatori(
        key,
        valoreKpiTarget.tipo,
        isSottoOpzModuloCompleto,
        notRisposta,
      );

      /** Costruisco lo stato disabilitato */
      const isDisabled =
        (key === 'idUnitaDiRiferimento' && parentKey === 'valoriKpi' && idx > 0) ||
        (this.isPermessoSoloRead() ?? false);

      /** Construisco il FormControl */
      config[key] = new FormControl({ value, disabled: isDisabled }, validatori);
    }
    return this.fb.group(config);
  }

  // ─── Validators ─────────────────────────────────────────────────────────────

  private _buildValidatori(
    formControlName: string,
    tipoTargetKpi?: string,
    isSottoOpzModuloCompleto?: boolean,
    notRisposta?: boolean,
  ): ValidatorFn[] {
    const validatori: ValidatorFn[] = [];
    const isTargetInPiano =
      tipoTargetKpi === 'AzioniEBuonePratiche' && this.compilazione().tipo === 'PIANO';
    const isTarget = tipoTargetKpi === 'valore_target' || isTargetInPiano;
    const minAnno = isTarget ? this.compilazione().esercizio[0] + 1 : 2000;

    if (formControlName === 'anno') {
      validatori.push(Validators.min(minAnno), Validators.max(9999));
    }

    const targetFields = ['idUnitaDiRiferimento', 'valoreAttribuito', 'anno', 'tipoAzione'];

    if (isTarget && targetFields.includes(formControlName)) {
      if (this.targetRequired()) validatori.push(Validators.required);
    } else {
      switch (formControlName) {
        case 'anno':
        case 'rispostaBreve':
          if (this.targetRequired())
            validatori.push(Validators.required, UtilityService.blankValidator);
          break;
        case 'valoreAttribuito':
          if (this.isPMI() && !isSottoOpzModuloCompleto && !notRisposta) {
            validatori.push(Validators.required, UtilityService.blankValidator);
          }
          break;
      }
    }

    return validatori;
  }

  // ─── Touched Marking ────────────────────────────────────────────────────────

  /**
   * Marca tutti i controlli del form come "toccati" (touched),
   * in modo da attivare la visualizzazione degli errori di validazione.
   * Distingue tra compilazioni di tipo REPORT_VSME e le altre tipologie.
   */
  private _markAllAsTouched(): void {
    this.ambitiFormArray.controls.forEach((ambito: any) => {
      ambito.controls['tematiche'].controls.forEach((tematica: any) => {
        if (this.compilazione().tipo === 'REPORT_VSME') {
          this._markRisposteVsmeAsTouched(tematica.controls);
        } else {
          this._markTematicaAsTouched(tematica.controls);
        }
      });
    });
  }

  /**
   * Marca come touched tutti i controlli di una tematica,
   * iterando sugli elementi definiti in ELEMENTI_DA_COMPILARE.
   * I KPI vengono gestiti tramite il metodo dedicato {@link _markKpisAsTouched}.
   *
   * @param controls - I controlli del FormGroup della tematica
   */
  private _markTematicaAsTouched(controls: any): void {
    for (const elemento of ELEMENTI_DA_COMPILARE) {
      if (elemento === 'kpis') {
        this._markKpisAsTouched(controls);
      } else if (controls[elemento]?.value?.length) {
        controls[elemento].controls.forEach((ctrl: any) => {
          Object.values(ctrl.controls).forEach((c: any) => c.markAsTouched());
        });
      }
    }
  }

  /**
   * Marca come touched tutti i controlli dei KPI presenti in una tematica,
   * inclusi i valori KPI e i valori target per ciascun elemento.
   *
   * @param controls - I controlli del FormGroup della tematica,
   *                   che deve contenere un FormArray `kpis`
   */
  private _markKpisAsTouched(controls: any): void {
    if (!controls?.kpis?.value?.length) return;

    controls.kpis.controls.forEach((elem: any) => {
      (['valoriKpi', 'valoriTarget'] as const).forEach((arrayKey) => {
        elem.controls?.[arrayKey]?.controls.forEach((group: any) => {
          Object.values(group.controls).forEach((c: any) => c.markAsTouched());
        });
      });
    });
  }

  /**
   * Marca come touched tutti i controlli delle risposte VSME di una tematica.
   * Per ciascuna sotto-opzione, il comportamento varia in base al tipo:
   *
   * - `checkbox` / `azione`: marca tutti i controlli figli del gruppo
   * - `kpi`: marca i controlli di `valoriKpi` e `valoriTarget`
   * - `dynamic_table`: non ancora implementato
   * - altri tipi: marca direttamente tutti i controlli della sotto-opzione
   *
   * @param controls - I controlli del FormGroup della tematica,
   *                   che deve contenere un FormArray `risposteVsme`
   */
  private _markRisposteVsmeAsTouched(controls: any): void {
    if (!controls?.risposteVsme?.value?.length) return;

    controls.risposteVsme.controls.forEach((risposta: any) => {
      risposta.controls?.sottoOpzioni?.controls.forEach((sottoOpz: any) => {
        const tipo = sottoOpz.controls?.['tipo']?.value;

        switch (tipo) {
          case 'checkbox':
          case 'azione':
            Object.values(sottoOpz.controls).forEach((ctrl: any) => {
              if (ctrl?.controls) {
                Object.values(ctrl.controls).forEach((c: any) => c.markAsTouched());
              } else {
                ctrl.markAsTouched();
              }
            });
            break;

          case 'kpi':
            (['valoriKpi', 'valoriTarget'] as const).forEach((arrayKey) => {
              sottoOpz.controls?.[arrayKey]?.controls.forEach((group: any) => {
                Object.values(group.controls).forEach((c: any) => c.markAsTouched());
              });
            });
            break;

          case 'dynamic_table':
            /* Da implementare */
            break;

          default:
            Object.values(sottoOpz.controls).forEach((c: any) => c.markAsTouched());
            break;
        }
      });
    });
  }

  // ─── Target Validation ──────────────────────────────────────────────────────

  private _hasTargetErrors(comp: any): boolean {
    let hasError = false;
    comp.ambiti?.forEach((ambito: any) => {
      ambito.tematiche?.forEach((tematica: any) => {
        tematica.kpis[0]?.valoriTarget?.forEach((valore: ValoriKpi) => {
          if (!valore.anno || !valore.valoreAttribuito || !valore.idUnitaDiRiferimento) {
            this.utilityService.openSnackBar('Uno o più target obbligatori non compilati', '', {
              duration: 2000,
              panelClass: ['red-snackbar'],
            });
            hasError = true;
          }
        });
      });
    });
    return hasError;
  }

  private _valutaTargetObbligatorio(): void {
    const { soglieSuperate, stdrBilancio, complianceBilancio } =
      this.bilancioService.controlloCompilazioneKpi;

    let stdrKpi: any[] = [];
    let complianceKpi: any[] = [];

    this.compilazione().ambiti?.forEach((ambito: any) => {
      ambito.tematiche?.forEach((tematica: any) => {
        stdrKpi = tematica?.kpis[0]?.stdr ?? [];
        complianceKpi = tematica?.kpis[0]?.compliance ?? [];
      });
    });

    const isGenerico = this.compilazione().settori[0]?.generico;
    const stdrPreselezione = this._filtraStdr(
      stdrKpi,
      complianceKpi,
      stdrBilancio,
      complianceBilancio,
      isGenerico,
    );

    const isConsigliato = stdrPreselezione.some(
      (s: any) =>
        (!s.settoriale && s?.target) || (s.settoriale && s.figli.some((f: any) => f.target)),
    );

    const isObbligatorio = stdrPreselezione.some(
      (s: any) =>
        (!s.settoriale && s?.target && s?.obbligatorio) ||
        (s.settoriale && s.figli.some((f: any) => f.target && s?.obbligatorio)),
    );

    if (isConsigliato) {
      this.targetConsigliato.set(true);
      if (soglieSuperate && isObbligatorio) {
        this.targetRequired.set(true);
        this.targetConsigliato.set(false);
      }
    }
  }

  private _filtraStdr(
    stdrKpi: any[],
    complianceKpi: any[],
    stdrBilancio: any[],
    complianceBilancio: any[],
    isGenerico: boolean,
  ): any[] {
    const matchStdrGenerico = (kpi: any, bilancio: any[]) =>
      bilancio.some((b) => b.id === kpi.id) && kpi.generale;

    const matchStdrSettoriale = (kpi: any, bilancio: any[]) =>
      bilancio.some(
        (b) =>
          (b.id === kpi.id && kpi.generale) ||
          b.figli?.some((fb: any) => kpi.figli?.some((fk: any) => fk.id === fb.id)),
      );

    const matchFn = isGenerico ? matchStdrGenerico : matchStdrSettoriale;

    let result = stdrKpi.filter((k) => matchFn(k, stdrBilancio));
    if (!result.length) result = complianceKpi.filter((k) => matchFn(k, complianceBilancio));
    return result;
  }

  // ─── Motivazione risposta Validation ──────────────────────────────────────────────────────

  private _isRispostaRequiredCompilata(comp: any): boolean {
    let isValid = false;
    comp.ambiti?.forEach((ambito: Ambito) => {
      ambito.tematiche?.forEach((tematica: Tematica) => {
        const risposteVsme: OpzioneTematica[] = tematica.risposteVsme ?? [];
        isValid = this.reportVsmeService.ctrRisposteVsmeCompilate(risposteVsme);
      });
    });

    return isValid;
  }

  // ─── UDM Loading ────────────────────────────────────────────────────────────

  private _caricaUnitaDiMisura(kpi: any): void {
    const idCategoria = kpi.idCategoriaUnitaDiMisura;
    if (!idCategoria || this.arrayUm[idCategoria]) return;

    this.arrayUm[idCategoria] = [];

    const filters: Filters[] = [
      { chiave: 'idCategoriaUnitaDiMisura', operator: 'eq', values: [idCategoria] },
    ];
    const tipo = this.compilazione().tipo as TipoCompilazione;
    const token = this.temporaryToken();

    let obs$;

    if (token) {
      // Scenario 1: Temporary token exists
      obs$ = this.unitaDiMisuraService.getUnitaDiMisuraConTempToken(token, 0, 1000, '', filters);
    } else if (['PIANO', 'BILANCIO', 'SINTESI'].includes(tipo)) {
      // Scenario 2: Get unita di misura specifiche per modulo
      obs$ = this.unitaDiMisuraService.getUnitaDiMisuraPerModulo(tipo, 0, 1000, '', filters);
    } else {
      // Scenario 3: Get unita di misura dalla libreria
      obs$ = this.unitaDiMisuraService.getUnitaDiMisura(0, 1000, '', filters);
    }

    obs$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((um) => (this.arrayUm[idCategoria] = um?.content ?? []));
  }

  // ─── File Management ────────────────────────────────────────────────────────

  private _getIdContesto(): string {
    const comp = this.compilazione();
    const map: Partial<Record<TipoCompilazione, string>> = {
      SINTESI: comp.sintesi?.[0],
      BILANCIO: comp.bilancio?.[0],
      POLICY: comp.policy?.[0],
    };
    return map[comp.tipo as TipoCompilazione] ?? '';
  }

  private _buildFileKey(
    idContesto: string,
    idAmbito: string,
    idTematica: string,
    idCertificazione: string,
  ): string {
    return `${idContesto}_${idAmbito}_${idTematica}_${idCertificazione}`;
  }

  private _addFileDaCaricare(
    idContesto: string,
    idAmbito: string,
    idTematica: string,
    idCertificazione: string,
    file: File | undefined,
  ): void {
    const key = this._buildFileKey(idContesto, idAmbito, idTematica, idCertificazione);
    const dati = {
      idAzienda: this.compilazione().aziende[0],
      idContesto,
      idAmbito,
      idTematica,
      idElemento: idCertificazione,
    };

    if (!file) {
      this._mappaFileDaCaricare[key] = { dati, file: undefined, url: undefined };
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this._mappaFileDaCaricare[key] = { dati, file, url: e.target?.result as string };
    };
    reader.readAsDataURL(file);
  }

  private _caricaFiles(files: FileDaCaricare[], index: number): Observable<boolean> {
    if (index >= files.length) return of(true);

    const { dati, file } = files[index];
    const tipo = this.compilazione().tipo as TipoCompilazione;
    const token = this.temporaryToken();
    const isEsterna = this.isCompEsterna();
    const data: { temporaryToken?: string } = token && isEsterna ? { temporaryToken: token } : {};

    const obs$ = file
      ? this._getUploadObs(tipo, file, data, dati, token, isEsterna)
      : this._getDeleteObs(tipo, dati, token, isEsterna);

    if (!obs$) {
      console.error('Nessun observable per salvataggio file:', files[index]);
      return of(false);
    }

    return obs$.pipe(switchMap(() => this._caricaFiles(files, index + 1)));
  }

  private _getUploadObs(
    tipo: TipoCompilazione,
    file: File,
    data: any,
    dati: any,
    token?: string,
    isEsterna?: boolean,
  ): Observable<any> | undefined {
    const svc = this.importazioniService;
    if (token && isEsterna) {
      const map: Partial<Record<TipoCompilazione, () => Observable<any>>> = {
        SINTESI: () =>
          svc
            .importImgCertificazioneSintesiDaEsterna(file, data, dati)
            .pipe(filter((r: any) => r?.type === 4)),
        BILANCIO: () =>
          svc
            .importImgCertificazioneBilancioDaEsterna(file, data, dati)
            .pipe(filter((r: any) => r?.type === 4)),
        POLICY: () =>
          svc
            .importImgCertificazionePolicyDaEsterna(file, data, dati)
            .pipe(filter((r: any) => r?.type === 4)),
      };
      return map[tipo]?.();
    } else {
      const map: Partial<Record<TipoCompilazione, () => Observable<any>>> = {
        SINTESI: () =>
          svc
            .importImgCertificazioneSintesi(file, data, dati)
            .pipe(filter((r: any) => r?.type === 4)),
        BILANCIO: () =>
          svc
            .importImgCertificazioneBilancio(file, data, dati)
            .pipe(filter((r: any) => r?.type === 4)),
        POLICY: () =>
          svc
            .importImgCertificazionePolicy(file, data, dati)
            .pipe(filter((r: any) => r?.type === 4)),
      };
      return map[tipo]?.();
    }
  }

  private _getDeleteObs(
    tipo: TipoCompilazione,
    dati: any,
    token?: string,
    isEsterna?: boolean,
  ): Observable<any> | undefined {
    const svc = this.importazioniService;
    if (token && isEsterna) {
      const map: Partial<Record<TipoCompilazione, () => Observable<any>>> = {
        BILANCIO: () => svc.deleteImgCertificazioneBilancioDaEsterna(dati, token),
        SINTESI: () => svc.deleteImgCertificazioneSintesiDaEsterna(dati, token),
        POLICY: () => svc.deleteImgCertificazionePolicyDaEsterna(dati, token),
      };
      return map[tipo]?.();
    } else {
      const map: Partial<Record<TipoCompilazione, () => Observable<any>>> = {
        BILANCIO: () => svc.deleteImgCertificazioneBilancio(dati),
        SINTESI: () => svc.deleteImgCertificazioneSintesi(dati),
        POLICY: () => svc.deleteImgCertificazionePolicy(dati),
      };
      return map[tipo]?.();
    }
  }
}
